import React from "react";
import cn from "classnames";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  InputProps,
  Label,
} from "reactstrap";
import { WebForm } from "../../../../cores/webForm/interfaces/api/get-web-form";
import { FormInput } from "../../../../cores/webForm/interfaces/api/post-public-form";
import { ContactInputType } from "../../../../cores/webForm/constants/InputType.constant";
import {
  getFormDesignClass,
  isCleanFormDesign,
} from "../../../../cores/webForm/constants/FormDesign.constant";
import {
  ACCEPTANCE_TYPE,
  getAcceptanceLinkUrls,
  getFormButtonJustify,
  getFormButtonWidth,
  getFormInputColumnClass,
  getFormInputCheckColumnClass,
  getFormInputHtmlType,
  isCategoryFormInput,
  isChoiceFormInput,
  isBooleanFormInput,
  normalizeFormInputOptions,
} from "../../../../cores/webForm/utils/formRender.utils";
import { renderAcceptanceLabel } from "./acceptanceLabel";
import CategorySelectField from "./CategorySelectField";
import "./FormFieldsView.scss";

interface Props {
  form: WebForm;
  inputs: FormInput[];
  inputErrors?: string[];
  showSuccessfulMessage?: boolean;
  readOnly?: boolean;
  submitting?: boolean;
  columnLayout?: boolean;
  className?: string;
  innerClassName?: string;
  onInputChange?: (input: FormInput, value: string) => void;
  onSubmit?: (event: React.FormEvent) => void;
}

export default function FormFieldsView({
  form,
  inputs,
  inputErrors = [],
  showSuccessfulMessage = false,
  readOnly = false,
  submitting = false,
  columnLayout = false,
  className,
  innerClassName,
  onInputChange,
  onSubmit,
}: Props) {
  const isClean = isCleanFormDesign(form.design);
  const buttonWidth = getFormButtonWidth(form.buttonSize);
  const fieldClassName = "web-form-fields__control";
  const accentColor = form.buttonColor || "#0066a2";

  const wrapSelect = (node: React.ReactNode, modifiers?: string) =>
    isClean ? (
      <div className={cn("web-form-fields__select-wrap", modifiers)}>{node}</div>
    ) : (
      node
    );

  const wrapWithColumn = (
    key: string,
    node: React.ReactNode,
    columnClass?: string,
    groupClassName?: string
  ) => {
    if (!columnLayout) {
      return node;
    }

    return (
      <FormGroup
        key={key}
        className={cn("w-100 web-form-fields__group", columnClass, groupClassName)}
      >
        {node}
      </FormGroup>
    );
  };

  const renderPlaceholderField = (
    input: FormInput,
    invalid: boolean,
    htmlType: InputProps["type"]
  ) => {
    const isPhone = input.type === ContactInputType.CONTACT_PHONE.id;
    const control = (
      <Input
        type={htmlType}
        name={`field-${input.id}`}
        id={`field-${input.id}`}
        disabled={readOnly}
        placeholder={input.placeholder}
        readOnly={readOnly}
        value={input.value || ""}
        className={cn(fieldClassName, {
          "borderRed is-invalid": invalid,
          "web-form-fields__control--phone": isPhone,
          "web-form-fields__control--textarea": htmlType === "textarea",
        })}
        onChange={(event) => onInputChange?.(input, event.target.value)}
      />
    );

    const field = isClean && isPhone ? (
      <div className="web-form-fields__select-wrap web-form-fields__select-wrap--phone">
        {control}
      </div>
    ) : (
      control
    );

    if (!columnLayout) {
      return (
        <FormGroup
          key={input.id}
          className={cn("w-100 web-form-fields__group", {
            "web-form-fields__group--clean": isClean,
          })}
        >
          {field}
        </FormGroup>
      );
    }

    return wrapWithColumn(
      input.id,
      field,
      getFormInputColumnClass(input.columnWidth),
      isClean ? "web-form-fields__group--clean" : undefined
    );
  };

  const renderModernCardField = (
    input: FormInput,
    invalid: boolean,
    htmlType: InputProps["type"]
  ) => {
    const fieldId = `field-${input.id}`;
    const control = (
      <>
        <Label className="web-form-fields__label" for={fieldId}>
          {input.placeholder}
        </Label>
        <Input
          type={htmlType}
          name={fieldId}
          id={fieldId}
          disabled={readOnly}
          placeholder={input.placeholder}
          readOnly={readOnly}
          value={input.value || ""}
          className={cn(fieldClassName, {
            "borderRed is-invalid": invalid,
            "web-form-fields__control--textarea": htmlType === "textarea",
          })}
          onChange={(event) => onInputChange?.(input, event.target.value)}
        />
      </>
    );

    if (!columnLayout) {
      return (
        <FormGroup key={input.id} className="w-100 web-form-fields__group">
          {control}
        </FormGroup>
      );
    }

    return wrapWithColumn(
      input.id,
      control,
      getFormInputColumnClass(input.columnWidth)
    );
  };

  const renderInput = (input: FormInput) => {
    const invalid = inputErrors.includes(input.id);
    const commonProps = {
      name: `field-${input.id}`,
      id: `field-${input.id}`,
      disabled: readOnly,
      className: cn(fieldClassName, { "borderRed is-invalid": invalid }),
    };

    if (isCategoryFormInput(input.type)) {
      return (
        <CategorySelectField
          key={input.id}
          input={input}
          invalid={invalid}
          readOnly={readOnly}
          clean={isClean}
          columnLayout={columnLayout}
          fieldClassName={fieldClassName}
          onInputChange={onInputChange}
        />
      );
    }

    if (isChoiceFormInput(input.type)) {
      const options = normalizeFormInputOptions(input.options);
      const select = (
        <Input
          {...commonProps}
          type="select"
          value={input.value || ""}
          className={cn(commonProps.className, {
            "web-form-fields__control--empty": isClean && !input.value,
          })}
          onChange={(event) => onInputChange?.(input, event.target.value)}
        >
          <option value="">{input.placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Input>
      );

      const field = (
        <>
          {!isClean && (
            <Label className="web-form-fields__label" for={`field-${input.id}`}>
              {input.placeholder}
            </Label>
          )}
          {wrapSelect(select)}
        </>
      );

      if (!columnLayout) {
        return (
          <FormGroup
            key={input.id}
            className={cn("w-100 web-form-fields__group", {
              "web-form-fields__group--clean": isClean,
            })}
          >
            {field}
          </FormGroup>
        );
      }

      return wrapWithColumn(
        input.id,
        field,
        getFormInputColumnClass(input.columnWidth),
        isClean ? "web-form-fields__group--clean" : undefined
      );
    }

    if (isBooleanFormInput(input.type)) {
      const label =
        input.type === ACCEPTANCE_TYPE
          ? input.placeholder ||
            "Acepto los Términos y Condiciones y confirmo haber leído la Política de Privacidad y Protección de Datos."
          : input.placeholder;
      const acceptanceLinks = getAcceptanceLinkUrls(input.options);

      return (
        <div
          key={input.id}
          className={cn(
            "web-form-fields__check-group",
            columnLayout ? getFormInputCheckColumnClass(input.columnWidth) : undefined,
            {
              "web-form-fields__check-group--clean": isClean,
            }
          )}
          style={isClean ? ({ "--form-accent": accentColor } as React.CSSProperties) : undefined}
        >
          <input
            type="checkbox"
            name={commonProps.name}
            id={commonProps.id}
            disabled={commonProps.disabled}
            className={cn("web-form-fields__check-input", { "is-invalid": invalid })}
            checked={input.value === "true"}
            onChange={(event) =>
              onInputChange?.(input, event.target.checked ? "true" : "")
            }
          />
          <label htmlFor={commonProps.id} className="web-form-fields__check-label">
            {renderAcceptanceLabel(label, acceptanceLinks)}
          </label>
        </div>
      );
    }

    const htmlType = getFormInputHtmlType(input.type) as InputProps["type"];

    if (isClean) {
      return renderPlaceholderField(input, invalid, htmlType);
    }

    return renderModernCardField(input, invalid, htmlType);
  };

  return (
    <Form onSubmit={onSubmit} className={className}>
      <div
        className={cn(
          "w-100 web-form-fields",
          getFormDesignClass(form.design),
          innerClassName
        )}
        style={isClean ? ({ "--form-accent": accentColor } as React.CSSProperties) : undefined}
      >
        {form.title && (
          <h2
            className={cn("text-wrap web-form-fields__title", {
              "text-center": !isClean,
              "web-form-fields__title--clean": isClean,
            })}
            style={{
              fontSize: form.titleSize,
              color: form.titleColor,
            }}
          >
            {form.title}
          </h2>
        )}

        {showSuccessfulMessage && (
          <Alert
            color="primary"
            className={cn({ "web-form-fields__alert--clean": isClean })}
            style={{ textAlign: "center" }}
          >
            {form.successfulMessage}
          </Alert>
        )}

        <div
          className={cn("web-form-fields__stack", {
            "web-form-fields__stack--columns": columnLayout,
          })}
        >
          {inputs.map(renderInput)}
        </div>

        {!readOnly && !submitting && (
          <div
            className={cn("d-flex z-index-50 web-form-fields__actions", {
              "web-form-fields__actions--clean": isClean,
            })}
            style={{ justifyContent: getFormButtonJustify(form.buttonPosition) }}
          >
            <button
              type="submit"
              className={cn("border-0 web-form-fields__submit", {
                "web-form-fields__submit--clean": isClean,
              })}
              style={{
                fontSize: form.buttonTextSize,
                width: isClean ? "100%" : buttonWidth,
                backgroundColor: form.buttonColor,
                color: form.buttonTextColor,
              }}
            >
              {form.buttonText}
            </button>
          </div>
        )}

        {readOnly && (
          <div
            className={cn("d-flex web-form-fields__actions", {
              "web-form-fields__actions--clean": isClean,
            })}
            style={{ justifyContent: getFormButtonJustify(form.buttonPosition) }}
          >
            <Button
              type="button"
              disabled
              className={cn("border-0 web-form-fields__submit", {
                "web-form-fields__submit--clean": isClean,
              })}
              style={{
                fontSize: form.buttonTextSize,
                width: isClean ? "100%" : buttonWidth,
                backgroundColor: form.buttonColor,
                color: form.buttonTextColor,
              }}
            >
              {form.buttonText}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
}
